import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import DashboardLayout from "../../layouts/DashboardLayout";

const TimeSlots = () => {
    const { propertyId } = useParams();
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [slots, setSlots] = useState([]);
    const [property, setProperty] = useState(null);
    const [bulkDate, setBulkDate] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [interval, setInterval] = useState(15); // default 15 minuten


    useEffect(() => {
        const fetchSlots = async () => {
            const ref = collection(db, "properties", propertyId, "timeslots");
            const snapshot = await getDocs(ref);
            const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            // Sorteren op basis van datum en tijd
            const sortedData = data.sort((a, b) => {
                const dateTimeA = new Date(`${a.date}T${a.time}`);
                const dateTimeB = new Date(`${b.date}T${b.time}`);
                return dateTimeA - dateTimeB;
            });

            setSlots(sortedData);
        };

        const fetchProperty = async () => {
            const ref = doc(db, "properties", propertyId);
            const snap = await getDoc(ref);
            if (snap.exists()) {
                setProperty({ id: snap.id, ...snap.data() });
            }
        };

        fetchSlots();
        fetchProperty();
    }, [propertyId]);

    const handleAddSlot = async (e) => {
        e.preventDefault();
        const ref = collection(db, "properties", propertyId, "timeslots");
        await addDoc(ref, {
            date,
            time,
            createdAt: serverTimestamp(),
            booked: false,
        });

        setDate("");
        setTime("");
        alert("Tijdslot toegevoegd!");
        window.location.reload();
    };

    const handleUpdateProperty = async () => {
        try {
            const ref = doc(db, "properties", property.id);
            await updateDoc(ref, {
                title: property.title,
                address: property.address,
                vraagprijs: property.vraagprijs,
                makelaarNaam: property.makelaarNaam,
                makelaarTel: property.makelaarTel,
                aangebodenDoor: property.aangebodenDoor,
                description: property.description,
            });
            alert("Woninggegevens opgeslagen ✅");
        } catch (err) {
            console.error(err);
            alert("Opslaan mislukt");
        }
    };

    const generateTimeSlots = (start, end, step) => {
        const slots = [];
        let [startH, startM] = start.split(":").map(Number);
        let [endH, endM] = end.split(":").map(Number);

        let current = new Date(0, 0, 0, startH, startM);
        const endTime = new Date(0, 0, 0, endH, endM);

        while (current < endTime) {
            const hours = `${current.getHours()}`.padStart(2, "0");
            const minutes = `${current.getMinutes()}`.padStart(2, "0");
            slots.push(`${hours}:${minutes}`);
            current.setMinutes(current.getMinutes() + step);
        }

        return slots;
    };

    const handleGenerateBulk = async (e) => {
        e.preventDefault();

        const slotTimes = generateTimeSlots(startTime, endTime, Number(interval));

        const ref = collection(db, "properties", propertyId, "timeslots");

        const promises = slotTimes.map((time) =>
            addDoc(ref, {
                date: bulkDate,
                time,
                createdAt: serverTimestamp(),
                booked: false,
            })
        );

        await Promise.all(promises);
        alert("Tijdsloten toegevoegd!");
        setBulkDate("");
        setStartTime("");
        setEndTime("");
        setInterval(15);
        window.location.reload();
    };

    return (
        <DashboardLayout>
            <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold mb-4">Tijdsloten beheren</h2>

                {/* 🏡 Woninggegevens bewerken */}
                {property && (
                    <div className="mb-8 bg-white p-4 rounded shadow space-y-4">
                        <h3 className="text-lg font-medium mb-2">Woninggegevens bewerken</h3>

                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            placeholder="Titel"
                            value={property.title}
                            onChange={(e) => setProperty({...property, title: e.target.value})}
                        />
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            placeholder="Adres"
                            value={property.address}
                            onChange={(e) => setProperty({...property, address: e.target.value})}
                        />
                        <textarea
                            className="w-full border rounded px-3 py-2"
                            placeholder="Beschrijving"
                            value={property.description}
                            onChange={(e) => setProperty({...property, description: e.target.value})}
                        />
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            placeholder="Vraagprijs"
                            value={property.vraagprijs}
                            onChange={(e) => setProperty({...property, vraagprijs: e.target.value})}
                        />
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            placeholder="Makelaarsnaam"
                            value={property.makelaarNaam}
                            onChange={(e) => setProperty({...property, makelaarNaam: e.target.value})}
                        />
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            placeholder="Telefoonnummer"
                            value={property.makelaarTel}
                            onChange={(e) => setProperty({...property, makelaarTel: e.target.value})}
                        />
                        <input
                            type="text"
                            className="w-full border rounded px-3 py-2"
                            placeholder="Aangeboden door"
                            value={property.aangebodenDoor}
                            onChange={(e) => setProperty({...property, aangebodenDoor: e.target.value})}
                        />
                        <button
                            onClick={handleUpdateProperty}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                        >
                            Woning opslaan
                        </button>
                    </div>
                )}

                {/* ⏰ Tijdslot toevoegen */}
                <form onSubmit={handleAddSlot} className="bg-white p-4 rounded shadow mb-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Datum</label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Tijd</label>
                        <input
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                    >
                        Tijdslot toevoegen
                    </button>
                </form>
                {/* 📆 Bulk tijdslot generator */}
                <form onSubmit={handleGenerateBulk} className="bg-white p-4 rounded shadow mb-6 space-y-4">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Bulk tijdsloten aanmaken</h4>
                    <div>
                        <label className="block text-sm font-medium mb-1">Datum</label>
                        <input
                            type="date"
                            value={bulkDate}
                            onChange={(e) => setBulkDate(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Begintijd</label>
                            <input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                required
                                className="w-full border border-gray-300 rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Eindtijd</label>
                            <input
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                required
                                className="w-full border border-gray-300 rounded px-3 py-2"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Duur per tijdslot (minuten)</label>
                        <input
                            type="number"
                            value={interval}
                            min={5}
                            onChange={(e) => setInterval(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded px-3 py-2"
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                    >
                        Genereer tijdsloten
                    </button>
                </form>


                {/* 📋 Bestaande slots */}
                <h3 className="text-lg font-medium mb-2">Bestaande tijdsloten</h3>
                <ul className="space-y-2">
                    {slots.map((slot) => (
                        <li
                            key={slot.id}
                            className="flex flex-col bg-white p-3 rounded border"
                        >
                            <div className="flex justify-between items-center">
                                <span>{slot.date} – {slot.time}</span>
                                <div className="flex items-center gap-2">
          <span
              className={`px-2 py-1 text-xs rounded font-medium ${
                  slot.booked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
              }`}
          >
            {slot.booked ? "Geboekt" : "Beschikbaar"}
          </span>
                                    <button
                                        onClick={async () => {
                                            const confirm = window.confirm(
                                                slot.booked
                                                    ? "Weet je zeker dat je deze boeking wilt verwijderen?"
                                                    : "Weet je zeker dat je dit tijdslot wilt verwijderen?"
                                            );
                                            if (!confirm) return;

                                            const ref = doc(db, "properties", propertyId, "timeslots", slot.id);

                                            if (slot.booked) {
                                                await updateDoc(ref, {
                                                    booked: false,
                                                    bookedBy: null,
                                                });
                                                alert("Boeking verwijderd.");
                                            } else {
                                                await deleteDoc(ref);
                                                alert("Tijdslot verwijderd.");
                                            }

                                            window.location.reload();
                                        }}
                                        className="text-red-500 text-xs underline"
                                    >
                                        {slot.booked ? "Boeking verwijderen" : "Slot verwijderen"}
                                    </button>
                                </div>
                            </div>

                            {slot.booked && slot.bookedBy && typeof slot.bookedBy === "object" && (
                                <div className="mt-2 text-sm text-gray-600 space-y-1">
                                    <div>📧 <strong>{slot.bookedBy.name}</strong> — <a href={`mailto:${slot.bookedBy.email}`} className="text-blue-600 hover:underline">{slot.bookedBy.email}</a></div>
                                    {slot.bookedBy.phone && (
                                        <div>📞 <a href={`tel:${slot.bookedBy.phone}`} className="text-blue-600 hover:underline">
                                            {slot.bookedBy.phone}
                                        </a></div>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </DashboardLayout>
    );
};

export default TimeSlots;